import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import PageHeader from '@/components/PageHeader';
import CountryForm from './country-form';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { Country } from '@/models/country.model';
import { getCountry } from '@/services/country.service';
import CountryGeometryForm from './country-geometry';
import CityList from '../../cities/list/city-list';
import CountryActions from './country-actions';

const CustomNav = ({ active, onSelect, newMode, ...props }) => {
  return (
    <Nav {...props} activeKey={active} onSelect={onSelect}>
      <Nav.Item eventKey="country-form">Country Detail</Nav.Item>
      <Nav.Item eventKey="country-geometry" disabled={newMode}>Country Geometry</Nav.Item>
      <Nav.Item eventKey="country-cities" disabled={newMode}>Cities</Nav.Item>
      <Nav.Item eventKey="country-actions" disabled={newMode}>Actions</Nav.Item>
    </Nav>
  );
};

const Page = () => {

  const requestedTab = useLocation()?.state?.tab;

  const [activeTab, setActiveTab] = useState('country-form');
  const [formObject, setFormObject] = useState<Country|undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;


  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);
  
  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<Country> = await getCountry(id);
        if (result.success) {
          setFormObject(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching Country: ${errorModel?.errorMessage}`);
        nav('/countries');
      }
      finally {
        setLoading(false);
      }
    }
  };
 
  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit && formObject ? `Edit Country: ${formObject.name}` : 'New Country'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href='/countries'> Countries</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" active={activeTab} onSelect={key => setActiveTab(key)} newMode={pageState == PageState.New} />
      {
        loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'country-form' && <CountryForm formObject={formObject} pageState={pageState}/>}
          {activeTab == 'country-geometry' && <CountryGeometryForm countryId={formObject?.id} jsonGeometry={formObject?.jsonGeometry} countryFetchData={fetchData}/>}
          {activeTab == 'country-cities' && <CityList countryId={formObject?.id}/>}
          {activeTab == 'country-actions' && <CountryActions formObject={formObject}/>}
        </>
      }
    </Panel>
  );
};

export default Page;
